import React, { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../../config";
import {
    Users, GraduationCap, BookOpen, Target, Calendar, Clock,
    ChevronRight, ChevronLeft, Loader2, AlertCircle, CheckCircle,
    FileText, BarChart3, RefreshCw, Building2, XCircle, MessageSquare,
    Award, X, ShieldCheck, Filter, ChevronDown, ChevronUp, Bell,
    AlertTriangle, Layers, Zap, HelpCircle, MapPin, Search, Eye,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import StatusHistoryTimeline from "../../Components/StatusHistoryTimeline";
import TeacherDiaryPanel from "../../Components/TeacherDiaryPanel";
import ApprovalsDashboard from "../../Components/approvals/ApprovalsDashboard";
import { ASSESSMENT_APPROVALS_CONFIG } from "../approvals/AssessmentApprovals";
import { EVENT_APPROVALS_CONFIG } from "../approvals/EventApprovals";
import { ACTIVITY_APPROVALS_CONFIG } from "../approvals/ActivityApprovals";

// ─── Constants ────────────────────────────────────────────────────────────────
const CONTENT_TYPES = [
    { key: "lesson-plans", label: "Lesson Plans",  icon: FileText,   color: "blue",   apiPending: "/hod/lesson-plans/pending?limit=100",  reviewPath: id => `/hod/lesson-plans/${id}/review` },
    { key: "assessments",  label: "Assessments",   icon: Target,     color: "purple", apiPending: "/hod/assessments/pending?limit=100",   reviewPath: id => `/hod/assessments/${id}/review` },
    { key: "activities",   label: "Activities",    icon: Zap,        color: "green",  apiPending: "/hod/activities/pending?limit=100",    reviewPath: id => `/hod/activities/${id}/review` },
    { key: "courses",      label: "Courses",       icon: Layers,     color: "indigo", apiPending: "/hod/courses/pending?limit=100",       reviewPath: id => `/hod/courses/${id}/review` },
    { key: "quizzes",      label: "Quizzes",       icon: HelpCircle, color: "orange", apiPending: "/hod/quizzes/pending?limit=100",       reviewPath: id => `/hod/quizzes/${id}/review` },
    { key: "events",       label: "Events",        icon: Calendar,   color: "rose",   apiPending: "/hod/events/pending?limit=100",        reviewPath: id => `/hod/events/${id}/review` },
];

const STATUS_STYLES = {
    pending_approval:  "bg-amber-100 text-amber-800",
    pending_admin:     "bg-orange-100 text-orange-800",
    changes_requested: "bg-purple-100 text-purple-800",
    submitted:        "bg-blue-100 text-blue-800",
    approved:         "bg-green-100 text-green-800",
    rejected:         "bg-red-100 text-red-700",
    active:           "bg-green-100 text-green-800",
    draft:            "bg-yellow-100 text-yellow-800",
    published:        "bg-teal-100 text-teal-800",
    completed:        "bg-purple-100 text-purple-800",
    taught:           "bg-indigo-100 text-indigo-800",
    archived:         "bg-gray-100 text-gray-600",
};
const STATUS_LABELS = {
    pending_approval: "Awaiting HOD Review", pending_admin: "Awaiting Admin Review",
    changes_requested: "Changes Requested",
    submitted: "Submitted", approved: "Approved",
    rejected: "Rejected", active: "Active", draft: "Draft", published: "Published",
    completed: "Completed", taught: "Taught", archived: "Archived",
};

// Content types that already run on the shared approval engine (HOD -> Admin, approved != published).
// Their tab renders the shared review screen; the older single-stage table below is only for types
// that have not been moved yet.
const GATED_TABS = { assessments: ASSESSMENT_APPROVALS_CONFIG, events: EVENT_APPROVALS_CONFIG, activities: ACTIVITY_APPROVALS_CONFIG };

// Lesson plans are a two-stage gate (Teacher -> HOD -> Admin) — everything
// else in this dashboard is the older single-stage {status, feedback}
// approve/reject contract. Keep the two paths clearly separate rather than
// forcing every content type through one shape.
// Display order + dot color for the clickable status-filter pills above the
// lesson-plan tables. Every real lesson-plan status, in workflow order.
const STATUS_FILTER_ORDER = [
    "draft", "pending_approval", "pending_admin", "changes_requested",
    "approved", "rejected", "taught", "published", "archived",
];
const STATUS_DOT = {
    draft: "bg-yellow-400",
    pending_approval: "bg-amber-400",
    pending_admin: "bg-orange-400",
    changes_requested: "bg-purple-400",
    approved: "bg-green-500",
    rejected: "bg-red-500",
    taught: "bg-indigo-400",
    published: "bg-teal-400",
    archived: "bg-gray-400",
};

// Shown in the Action column for a row that isn't actionable right now, so
// it's clear WHY there's no Review button instead of it just looking broken.
const NOT_ACTIONABLE_HINT = {
    draft: "Not submitted yet",
    changes_requested: "Waiting on teacher",
    rejected: "Closed",
    pending_admin: "With admin",
    pending_approval: "With HOD",
    approved: "Approved",
    taught: "Taught",
    archived: "Archived",
};

const LESSON_PLAN_ACTIONS = [
    { action: "approve", label: "Approve", color: "green", Icon: CheckCircle },
    { action: "request_changes", label: "Request Changes", color: "purple", Icon: AlertTriangle },
    { action: "reject", label: "Reject", color: "red", Icon: XCircle },
];

const getItemMeta = (item, key) => {
    switch (key) {
        case "lesson-plans": return { col1: item.subject, col2: item.grade, col3: item.class_name };
        case "assessments":  return { col1: item.type,    col2: item.grade, col3: item.subject };
        case "activities":   return { col1: item.domain,  col2: item.grade, col3: null };
        case "courses":      return { col1: item.course_type, col2: item.curriculum_stage, col3: item.hours_to_complete ? `${item.hours_to_complete}h` : null };
        case "quizzes":      return { col1: item.subject, col2: item.grade, col3: item.total_questions ? `${item.total_questions} Qs` : null };
        case "events":       return { col1: item.event_type, col2: item.start_date ? new Date(item.start_date).toLocaleDateString() : null, col3: item.location };
        default: return {};
    }
};

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>
        {STATUS_LABELS[status] || status}
    </span>
);

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ msg, type, onClose }) => (
    <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 ${type === "success" ? "bg-green-500" : "bg-red-500"} text-white`}
        style={{ animation: "slideInRight 0.3s ease-out" }}>
        {type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
        <span>{msg}</span>
        <button onClick={onClose}><X className="w-4 h-4" /></button>
    </div>
);

// ─── Review Modal ─────────────────────────────────────────────────────────────
// `isLessonPlanGate` switches this between the OLD single-stage
// {status, feedback} approve/reject contract (assessments, activities,
// courses, quizzes, events — unchanged) and the NEW two-stage lesson-plan
// contract: {action: approve|request_changes|reject, reason}, where a
// reason is REQUIRED for anything but approve, and self-review is blocked
// server-side regardless of what this form allows.
const ReviewModal = ({ item, contentLabel, isLessonPlanGate, stageLabel, onClose, onConfirm, saving }) => {
    const [status, setStatus] = useState("approved");
    const [action, setAction] = useState("approve");
    const [feedback, setFeedback] = useState("");
    const [touchedReason, setTouchedReason] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const meta = getItemMeta(item, item._contentKey || "");

    const reasonRequired = isLessonPlanGate && action !== "approve";
    const reasonMissing = reasonRequired && !feedback.trim();

    const handleConfirmClick = () => {
        if (reasonMissing) { setTouchedReason(true); return; }
        if (isLessonPlanGate) onConfirm(action, feedback || null);
        else onConfirm(status, feedback || null);
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#D67C2C] flex items-center justify-center">
                            <ShieldCheck className="w-5 h-5 text-white" />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-900">
                            Review {contentLabel}{stageLabel ? ` — ${stageLabel}` : ""}
                        </h2>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    {/* Item preview */}
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <p className="font-semibold text-gray-900">{item.title}</p>
                        <p className="text-sm text-gray-500 mt-0.5">by {item.teacher_name || "Teacher"}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                            {meta.col1 && <span className="capitalize">{meta.col1}</span>}
                            {meta.col2 && <span className="flex items-center gap-1"><GraduationCap size={11} />{meta.col2}</span>}
                            {meta.col3 && <span>{meta.col3}</span>}
                            {item.created_at && <span className="flex items-center gap-1"><Clock size={11} />{new Date(item.created_at).toLocaleDateString()}</span>}
                        </div>
                        {isLessonPlanGate && item.hod_feedback && (
                            <p className="text-xs text-gray-500 mt-2 border-t border-gray-200 pt-2">
                                <span className="font-medium">HOD notes:</span> {item.hod_feedback}
                            </p>
                        )}
                    </div>

                    {/* This modal only ever showed metadata — subject, grade,
                        teacher, dates. A reviewer must read the actual content
                        (objectives, lesson flow, assessment plan, etc.) before
                        deciding, not just approve off a title. Opens in a new
                        tab so the review dialog here stays open to come back to. */}
                    {isLessonPlanGate && (
                        <a
                            href={`/LessonPlanManagement?planId=${item.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border-2 border-[#D67C2C] text-[#D67C2C] text-sm font-semibold hover:bg-orange-50 transition-colors"
                        >
                            <Eye className="w-4 h-4" /> View Full Lesson Plan (opens in a new tab)
                        </a>
                    )}

                    {/* Diff since it was last approved (only present once a plan
                        that had been approved was materially edited again) */}
                    {isLessonPlanGate && Array.isArray(item.changes_since_approval) && item.changes_since_approval.length > 0 && (
                        <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-3">
                            <p className="text-xs font-semibold text-indigo-800 mb-1">Changed since it was last approved:</p>
                            <ul className="text-xs text-indigo-700 list-disc list-inside space-y-0.5">
                                {item.changes_since_approval.map(c => (
                                    <li key={c.field}><span className="font-medium">{c.field}</span></li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Activity history — every status change this plan has been
                        through, so a reviewer isn't deciding blind on a plan that,
                        say, was already rejected once before. */}
                    {isLessonPlanGate && Array.isArray(item.status_history) && item.status_history.length > 0 && (
                        <div className="rounded-xl border border-gray-200">
                            <button type="button" onClick={() => setShowHistory(s => !s)}
                                className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50">
                                <span>Activity History ({item.status_history.length})</span>
                                <span>{showHistory ? "Hide" : "Show"}</span>
                            </button>
                            {showHistory && (
                                <div className="px-3 pb-3 pt-1 border-t border-gray-100">
                                    <StatusHistoryTimeline history={item.status_history} />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Decision */}
                    <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Decision</p>
                        {isLessonPlanGate ? (
                            <div className="grid grid-cols-3 gap-2">
                                {LESSON_PLAN_ACTIONS.map(({ action: a, label, Icon }) => (
                                    <button key={a} onClick={() => { setAction(a); setTouchedReason(false); }}
                                        className={`flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl border-2 text-xs font-medium transition-all
                                            ${action === a
                                                ? (a === "approve" ? "bg-green-50 border-green-400 text-green-700" : a === "reject" ? "bg-red-50 border-red-400 text-red-600" : "bg-purple-50 border-purple-400 text-purple-700")
                                                : "border-gray-200 text-gray-500 hover:border-gray-300"}`}>
                                        <Icon size={16} />
                                        {label}
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="flex gap-3">
                                {["approved", "rejected"].map(s => (
                                    <button key={s} onClick={() => setStatus(s)}
                                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-medium transition-all
                                            ${status === s ? (s === "approved" ? "bg-green-50 border-green-400 text-green-700" : "bg-red-50 border-red-400 text-red-600") : "border-gray-200 text-gray-500 hover:border-gray-300"}`}>
                                        {s === "approved" ? <CheckCircle size={16} /> : <XCircle size={16} />}
                                        {s === "approved" ? "Approve" : "Reject"}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {/* Feedback / reason */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            <MessageSquare size={13} className="inline mr-1.5 text-gray-400" />
                            {reasonRequired ? "Reason " : "Feedback "}
                            <span className={`font-normal text-xs ${reasonRequired ? "text-red-500" : "text-gray-400"}`}>
                                {reasonRequired ? "(required)" : "(optional)"}
                            </span>
                        </label>
                        <textarea value={feedback} onChange={e => { setFeedback(e.target.value); setTouchedReason(false); }} rows={3}
                            placeholder={isLessonPlanGate
                                ? (action === "reject" ? "Explain why this plan is rejected..." : action === "request_changes" ? "Explain exactly what needs to change..." : "Add comments (optional)...")
                                : (status === "rejected" ? "Explain what needs to change..." : "Add comments...")}
                            className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent resize-none ${touchedReason && reasonMissing ? "border-red-400" : "border-gray-300"}`} />
                        {touchedReason && reasonMissing && (
                            <p className="text-xs text-red-600 mt-1">A reason is required for this decision.</p>
                        )}
                    </div>
                </div>
                <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100">
                    <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors">Cancel</button>
                    <button onClick={handleConfirmClick} disabled={saving}
                        className={`flex items-center gap-2 px-5 py-2 rounded-lg font-medium transition-all duration-200 disabled:opacity-60
                            ${(isLessonPlanGate ? action : status) === "approve" || (isLessonPlanGate ? action : status) === "approved"
                                ? "bg-green-600 hover:bg-green-700 text-white"
                                : (isLessonPlanGate ? action : status) === "request_changes" ? "bg-purple-600 hover:bg-purple-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}`}>
                        {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                        {saving ? "Saving..." : (isLessonPlanGate
                            ? LESSON_PLAN_ACTIONS.find(a => a.action === action)?.label
                            : (status === "approved" ? "Approve" : "Reject"))}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Teacher Row (expandable) ─────────────────────────────────────────────────
const TeacherRow = ({ teacher, onViewContent }) => {
    const [expanded, setExpanded] = useState(false);
    const initials = teacher.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
    const pending = (teacher.pending_lp_count || 0) + (teacher.pending_assess_count || 0);
    return (
        <>
            <tr className={`hover:bg-orange-50/40 transition-colors cursor-pointer ${expanded ? "bg-orange-50/30" : ""}`} onClick={() => setExpanded(e => !e)}>
                <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#D67C2C] to-orange-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                            {initials}
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900 text-sm">{teacher.name}</span>
                                {pending > 0 && (
                                    <span className="flex items-center gap-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                                        <Bell size={9} />{pending}
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-gray-400">{teacher.email}</div>
                        </div>
                    </div>
                </td>
                <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-1">
                        {teacher.grade_levels?.length > 0
                            ? teacher.grade_levels.map(g => <span key={g} className="px-1.5 py-0.5 bg-orange-50 text-orange-700 text-[10px] font-medium rounded border border-orange-100">{g}</span>)
                            : <span className="text-xs text-gray-400">—</span>}
                    </div>
                </td>
                <td className="px-4 py-4 text-sm font-semibold text-gray-800">{teacher.classes_count}</td>
                <td className="px-4 py-4 text-sm font-semibold text-gray-800">{teacher.lesson_plans_count}</td>
                <td className="px-4 py-4 text-sm font-semibold text-gray-800">{teacher.assessments_count}</td>
                <td className="px-4 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${teacher.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                        {teacher.is_active ? "Active" : "Inactive"}
                    </span>
                </td>
                <td className="px-4 py-4">
                    <button className="p-1.5 text-gray-400 hover:text-[#D67C2C] hover:bg-orange-50 rounded-lg transition-colors">
                        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                </td>
            </tr>
            {expanded && (
                <tr className="bg-orange-50/20">
                    <td colSpan={7} className="px-6 py-4 border-t border-orange-100">
                        <div className="flex items-center gap-3">
                            <span className="text-xs font-medium text-gray-500">View content:</span>
                            <button onClick={e => { e.stopPropagation(); onViewContent(teacher, "lessonPlans"); }}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:border-[#D67C2C] hover:text-[#D67C2C] transition-colors">
                                <FileText size={12} /> Lesson Plans {teacher.pending_lp_count > 0 && <span className="bg-amber-100 text-amber-700 text-[10px] px-1 rounded-full">{teacher.pending_lp_count}</span>}
                            </button>
                            <button onClick={e => { e.stopPropagation(); onViewContent(teacher, "assessments"); }}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:border-[#D67C2C] hover:text-[#D67C2C] transition-colors">
                                <Target size={12} /> Assessments {teacher.pending_assess_count > 0 && <span className="bg-amber-100 text-amber-700 text-[10px] px-1 rounded-full">{teacher.pending_assess_count}</span>}
                            </button>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

// ─── Content Table (pending items for any content type) ───────────────────────
// `onBulkApprove(ids)`, when given, turns on row checkboxes and an
// "Approve Selected" bar — used only for the lesson-plan queues, since
// that's the only content type with a bulk-review endpoint.
// `actionableStatuses`: null/undefined means every row is actionable (the
// old single-stage content types, where "pending" already implies
// actionable). For lesson plans, pass the one status this reviewer can act
// on right now (e.g. ['pending_approval']) — every other row still shows,
// just without a Review button or bulk checkbox, so a plan a HOD sent back
// for changes stays visible instead of silently disappearing once it
// leaves the one status the old queue-only view filtered on.
// Read-only Teacher's Diary for a lesson plan that has been taught. Available to
// every reviewer role (HOD, admin, management); the server scopes which plans
// each of them can load in the first place.
const DiaryModal = ({ item, onClose }) => (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-start justify-between p-5 border-b border-gray-100">
                <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 truncate">{item.title}</h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {[item.teacher_name && `by ${item.teacher_name}`, item.grade, item.subject].filter(Boolean).join(" · ")}
                    </p>
                </div>
                <button onClick={onClose} aria-label="Close" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <X className="w-5 h-5 text-gray-500" />
                </button>
            </div>
            <div className="p-5 overflow-y-auto space-y-4">
                <TeacherDiaryPanel plan={item} />
                <a
                    href={`/LessonPlanManagement?planId=${item.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border-2 border-[#D67C2C] text-[#D67C2C] text-sm font-semibold hover:bg-orange-50 transition-colors"
                >
                    <Eye className="w-4 h-4" /> View Full Lesson Plan (opens in a new tab)
                </a>
            </div>
        </div>
    </div>
);

const ContentTable = ({
    items, contentKey, onReview, onBulkApprove, bulkSaving, icon,
    actionableStatuses = null, emptyTitle = "All caught up!", emptyText,
    statusFilter: controlledStatusFilter, onStatusFilterChange,
    // When a status isn't actionable HERE but this account could act on it in
    // a different tab (e.g. this is the HOD-stage table and the plan has
    // already moved to 'pending_admin'), map that status to a callback that
    // jumps there — instead of a dead-end "With admin" label.
    jumpToTabForStatus = {},
    // Opens the read-only Teacher's Diary for plans that have been taught.
    onViewDiary = null,
}) => {
    const ct = CONTENT_TYPES.find(c => c.key === contentKey);
    const Icon = icon || ct?.icon || FileText;
    const bulkEnabled = typeof onBulkApprove === "function";
    const isActionable = (status) => !actionableStatuses || actionableStatuses.includes(status);

    const [search, setSearch] = useState("");
    const [internalStatusFilter, setInternalStatusFilter] = useState("");
    const statusFilter = controlledStatusFilter !== undefined ? controlledStatusFilter : internalStatusFilter;
    const setStatusFilter = onStatusFilterChange || setInternalStatusFilter;
    const [dateStart, setDateStart] = useState("");
    const [dateEnd, setDateEnd] = useState("");
    const [selected, setSelected] = useState([]);

    const statusOptions = [...new Set(items.map(i => i.status).filter(Boolean))];

    const filteredItems = items.filter(item => {
        if (statusFilter && item.status !== statusFilter) return false;
        if (search) {
            const q = search.toLowerCase();
            const matches = (item.title || "").toLowerCase().includes(q) || (item.teacher_name || "").toLowerCase().includes(q);
            if (!matches) return false;
        }
        if (dateStart || dateEnd) {
            if (!item.created_at) return false;
            const created = new Date(item.created_at);
            if (dateStart && created < new Date(dateStart)) return false;
            if (dateEnd && created > new Date(`${dateEnd}T23:59:59.999`)) return false;
        }
        return true;
    });

    if (items.length === 0) {
        return (
            <div className="text-center py-16">
                <CheckCircle className="w-16 h-16 text-green-200 mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium">{emptyTitle}</p>
                <p className="text-gray-400 text-sm mt-1">{emptyText || `No ${ct?.label?.toLowerCase()} to show`}</p>
            </div>
        );
    }

    return (
        <div>
            {bulkEnabled && selected.length > 0 && (
                <div className="px-5 py-3 bg-orange-50 border-b border-orange-100 flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-orange-800">{selected.length} selected</span>
                    <div className="flex items-center gap-3">
                        <button onClick={() => setSelected([])} className="text-sm text-gray-600 hover:underline">Clear</button>
                        <button
                            onClick={() => { onBulkApprove(selected); setSelected([]); }}
                            disabled={bulkSaving}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-60">
                            {bulkSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                            Approve Selected ({selected.length})
                        </button>
                    </div>
                </div>
            )}
            <div className="p-5 border-b border-gray-100 bg-gray-50">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-[#D67C2C]" />
                        <span className="text-sm font-medium text-gray-700">Filters</span>
                    </div>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="text" placeholder="Search title or teacher..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent w-56" />
                    </div>
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent">
                        <option value="">All Status</option>
                        {statusOptions.map(s => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
                    </select>
                    <input type="date" value={dateStart} onChange={e => setDateStart(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent" />
                    <span className="text-gray-400 text-sm">to</span>
                    <input type="date" value={dateEnd} onChange={e => setDateEnd(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent" />
                    {(search || statusFilter || dateStart || dateEnd) && (
                        <button onClick={() => { setSearch(""); setStatusFilter(""); setDateStart(""); setDateEnd(""); }}
                            className="text-sm text-[#D67C2C] hover:underline font-medium">
                            Clear filters
                        </button>
                    )}
                </div>
            </div>
            {filteredItems.length === 0 ? (
                <div className="text-center py-16">
                    <Search className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg font-medium">No {ct?.label?.toLowerCase()} match your filters</p>
                    <p className="text-gray-400 text-sm mt-1">Try adjusting your filters</p>
                </div>
            ) : (
            <div className="overflow-x-auto">
            <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        {bulkEnabled && (
                            <th className="px-4 py-3 w-10">
                                <input type="checkbox"
                                    checked={(() => {
                                        const actionableIds = filteredItems.filter(i => isActionable(i.status)).map(i => i.id);
                                        return actionableIds.length > 0 && actionableIds.every(id => selected.includes(id));
                                    })()}
                                    onChange={e => setSelected(e.target.checked ? filteredItems.filter(i => isActionable(i.status)).map(i => i.id) : [])}
                                />
                            </th>
                        )}
                        {["Title", "Teacher", "Details", "Date", "Status", "Action"].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                    {filteredItems.map(item => {
                        const meta = getItemMeta(item, contentKey);
                        return (
                            <tr key={item.id} className="hover:bg-orange-50/30 transition-colors">
                                {bulkEnabled && (
                                    <td className="px-4 py-4">
                                        {isActionable(item.status) ? (
                                            <input type="checkbox"
                                                checked={selected.includes(item.id)}
                                                onChange={e => setSelected(prev => e.target.checked ? [...prev, item.id] : prev.filter(id => id !== item.id))}
                                            />
                                        ) : (
                                            <input type="checkbox" disabled className="opacity-30" />
                                        )}
                                    </td>
                                )}
                                <td className="px-4 py-4">
                                    <div className="flex items-center gap-2">
                                        <div className="w-7 h-7 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                                            <Icon size={13} className="text-[#D67C2C]" />
                                        </div>
                                        <span className="font-medium text-gray-900 text-sm max-w-xs truncate">{item.title}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-4">
                                    <span className="text-sm text-gray-700">{item.teacher_name || "—"}</span>
                                </td>
                                <td className="px-4 py-4">
                                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                                        {meta.col1 && <span className="capitalize">{meta.col1}</span>}
                                        {meta.col2 && <span className="flex items-center gap-0.5"><GraduationCap size={10} />{meta.col2}</span>}
                                        {meta.col3 && <span>{meta.col3}</span>}
                                    </div>
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-500">
                                    {item.created_at ? new Date(item.created_at).toLocaleDateString() : "—"}
                                </td>
                                <td className="px-4 py-4"><StatusBadge status={item.status} /></td>
                                <td className="px-4 py-4">
                                    {isActionable(item.status) ? (
                                        <button onClick={() => onReview(item)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#D67C2C] to-orange-600 text-white rounded-lg text-xs font-medium hover:shadow-md transition-all">
                                            <ShieldCheck size={12} /> Review
                                        </button>
                                    ) : item.status === "taught" && onViewDiary ? (
                                        <button onClick={() => onViewDiary(item)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 border border-indigo-300 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium hover:bg-indigo-100 transition-colors">
                                            <BookOpen size={12} /> Teacher's Diary
                                        </button>
                                    ) : jumpToTabForStatus[item.status] ? (
                                        <button onClick={jumpToTabForStatus[item.status]}
                                            className="flex items-center gap-1 text-xs text-[#D67C2C] font-medium hover:underline">
                                            {NOT_ACTIONABLE_HINT[item.status] || "Review elsewhere"} <ChevronRight size={12} />
                                        </button>
                                    ) : (
                                        <span className="text-xs text-gray-400">{NOT_ACTIONABLE_HINT[item.status] || "—"}</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            </div>
            )}
        </div>
    );
};

// ─── Teacher Content Drawer ───────────────────────────────────────────────────
const TeacherContentDrawer = ({ teacher, contentType, items, loading, onClose, onReview, onViewDiary }) => (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#D67C2C] flex items-center justify-center">
                        {contentType === "lessonPlans" ? <FileText className="w-5 h-5 text-white" /> : <Target className="w-5 h-5 text-white" />}
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">{contentType === "lessonPlans" ? "Lesson Plans" : "Assessments"}</h2>
                        <p className="text-sm text-gray-500">{teacher.name}</p>
                    </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="overflow-y-auto flex-1">
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <RefreshCw className="w-8 h-8 text-[#D67C2C] animate-spin" />
                        <span className="ml-3 text-gray-600">Loading...</span>
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center py-16">
                        <Layers className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                        <p className="text-gray-400">No items found</p>
                    </div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                            <tr>
                                {["Title", "Grade / Subject", "Date", "Status", "Action"].map(h => (
                                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {items.map(item => (
                                <tr key={item.id} className="hover:bg-orange-50/30 transition-colors">
                                    <td className="px-4 py-3">
                                        <span className="font-medium text-gray-900 text-sm">{item.title}</span>
                                        {item.hod_feedback && <p className="text-xs text-gray-400 mt-0.5 italic">"{item.hod_feedback}"</p>}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-gray-500">
                                        {[item.grade, item.subject || item.domain].filter(Boolean).join(" · ") || "—"}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-gray-500">
                                        {item.created_at ? new Date(item.created_at).toLocaleDateString() : "—"}
                                    </td>
                                    <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
                                    <td className="px-4 py-3">
                                        {(contentType === "lessonPlans"
                                            ? item.status === "pending_approval"
                                            : (item.status === "pending_approval" || item.status === "submitted" || item.status === "draft")
                                        ) && (
                                            <button onClick={() => onReview(item)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#D67C2C] to-orange-600 text-white rounded-lg text-xs font-medium hover:shadow-md transition-all">
                                                <ShieldCheck size={12} /> Review
                                            </button>
                                        )}
                                        {contentType === "lessonPlans" && item.status === "taught" && (
                                            <button onClick={() => onViewDiary(item)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 border border-indigo-300 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium hover:bg-indigo-100 transition-colors">
                                                <BookOpen size={12} /> Teacher's Diary
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    </div>
);

// ─── Main HOD Dashboard ───────────────────────────────────────────────────────
const HODDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [myDepartment, setMyDepartment] = useState(null);
    const [overview, setOverview] = useState(null);
    const [teacherList, setTeacherList] = useState([]);
    const [lpStats, setLpStats] = useState(null);
    const [pendingContent, setPendingContent] = useState(
        Object.fromEntries(CONTENT_TYPES.map(ct => [ct.key, []]))
    );

    const [drawer, setDrawer] = useState(null);
    const [reviewModal, setReviewModal] = useState(null);
    const [diaryItem, setDiaryItem] = useState(null);
    const [reviewSaving, setReviewSaving] = useState(false);
    const [bulkSaving, setBulkSaving] = useState(false);

    const [tabParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(tabParams.get("tab") || "teachers");
    const [gradeFilter, setGradeFilter] = useState("");
    const [searchFilter, setSearchFilter] = useState("");
    const [teacherStatusFilter, setTeacherStatusFilter] = useState("");

    // Second, independent approval stage — deliberately restricted to
    // admin/management. A plain HOD only ever sees their stage-1 queue.
    const userRole = localStorage.getItem("user_role");
    const canReviewAdminStage = userRole === "admin" || userRole === "management";
    const [pendingAdminLessonPlans, setPendingAdminLessonPlans] = useState([]);
    // Every lesson plan in scope, any status — backs the browsing table so a
    // plan doesn't just vanish once it leaves 'pending_approval' (e.g. after
    // "Request Changes"). Separate from pendingContent/pendingAdminLessonPlans,
    // which stay pending-only and drive the actionable badge counts.
    const [allLessonPlans, setAllLessonPlans] = useState([]);
    const [allAdminLessonPlans, setAllAdminLessonPlans] = useState([]);
    const [lpStatusFilter, setLpStatusFilter] = useState("");
    const [lpAdminStatusFilter, setLpAdminStatusFilter] = useState("");

    const token = localStorage.getItem("accessToken");

    const notify = (type, msg) => {
        if (type === "success") { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); }
        else { setError(msg); setTimeout(() => setError(null), 5000); }
    };

    const apiFetch = useCallback(async (path, options = {}) => {
        const res = await fetch(`${API_BASE_URL}${path}`, {
            ...options,
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) },
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || `HTTP ${res.status}`);
        }
        return res.json();
    }, [token]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const results = await Promise.allSettled([
                apiFetch("/admin/departments/my-department"),
                apiFetch("/hod/teachers/overview"),
                apiFetch("/hod/teachers"),
                apiFetch("/hod/stats/lesson-plans"),
                ...CONTENT_TYPES.map(ct => apiFetch(ct.apiPending)),
                canReviewAdminStage ? apiFetch("/hod/admin/lesson-plans/pending?limit=100") : Promise.resolve([]),
                apiFetch("/hod/lesson-plans/all?limit=200"),
                canReviewAdminStage ? apiFetch("/hod/admin/lesson-plans/all?limit=200") : Promise.resolve([]),
            ]);
            const [dept, ov, teachers, stats, ...rest] = results;
            const pendingResults = rest.slice(0, CONTENT_TYPES.length);
            const adminStageResult = rest[CONTENT_TYPES.length];
            const allLpResult = rest[CONTENT_TYPES.length + 1];
            const allAdminLpResult = rest[CONTENT_TYPES.length + 2];

            if (dept.status === "fulfilled") setMyDepartment(dept.value);
            if (ov.status === "fulfilled") setOverview(ov.value);
            if (stats.status === "fulfilled") setLpStats(stats.value);
            if (adminStageResult?.status === "fulfilled" && Array.isArray(adminStageResult.value)) {
                setPendingAdminLessonPlans(adminStageResult.value);
            }
            if (allLpResult?.status === "fulfilled" && Array.isArray(allLpResult.value)) {
                setAllLessonPlans(allLpResult.value);
            }
            if (allAdminLpResult?.status === "fulfilled" && Array.isArray(allAdminLpResult.value)) {
                setAllAdminLessonPlans(allAdminLpResult.value);
            }

            const teacherArr = teachers.status === "fulfilled" && Array.isArray(teachers.value) ? teachers.value : [];
            const newPending = {};
            CONTENT_TYPES.forEach((ct, i) => {
                newPending[ct.key] = pendingResults[i].status === "fulfilled" && Array.isArray(pendingResults[i].value) ? pendingResults[i].value : [];
            });
            setPendingContent(newPending);

            const lpArr = newPending["lesson-plans"] || [];
            const assArr = newPending["assessments"] || [];
            setTeacherList(teacherArr.map(t => ({
                ...t,
                pending_lp_count: lpArr.filter(x => x.teacher_id === t.id).length,
                pending_assess_count: assArr.filter(x => x.teacher_id === t.id).length,
            })));
        } catch (e) {
            notify("error", `Failed to load data: ${e.message}`);
        } finally {
            setLoading(false);
        }
    }, [apiFetch, canReviewAdminStage]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleViewTeacherContent = useCallback(async (teacher, contentType) => {
        setDrawer({ teacher, contentType, items: [], loading: true });
        try {
            const path = contentType === "lessonPlans"
                ? `/hod/teachers/${teacher.id}/lesson-plans`
                : `/hod/teachers/${teacher.id}/assessments`;
            const data = await apiFetch(path);
            setDrawer(prev => prev ? { ...prev, items: Array.isArray(data) ? data : [], loading: false } : null);
        } catch {
            setDrawer(prev => prev ? { ...prev, items: [], loading: false } : null);
        }
    }, [apiFetch]);

    // contentKey "lesson-plans-admin" is a synthetic key (not in CONTENT_TYPES)
    // for the second, admin-only review stage on the same lesson plans.
    const openReview = (item, contentKey) => {
        setReviewModal({ item: { ...item, _contentKey: contentKey === "lesson-plans-admin" ? "lesson-plans" : contentKey }, contentKey });
    };

    const handleConfirmReview = async (statusOrAction, feedbackOrReason) => {
        if (!reviewModal) return;
        setReviewSaving(true);
        try {
            const { item, contentKey } = reviewModal;

            if (contentKey === "lesson-plans" || contentKey === "lesson-plans-admin") {
                const path = contentKey === "lesson-plans-admin"
                    ? `/hod/admin/lesson-plans/${item.id}/review`
                    : `/hod/lesson-plans/${item.id}/review`;
                await apiFetch(path, { method: "PATCH", body: JSON.stringify({ action: statusOrAction, reason: feedbackOrReason }) });
                notify("success", `Lesson plan ${statusOrAction.replace("_", " ")}d`);
                setReviewModal(null);
                // Refetch everything rather than patch pieces of state by hand —
                // a review can move a plan between the pending queue, the
                // browse-all table, AND the admin-stage queue in one action, and
                // keeping all three in sync piecemeal was the actual bug behind
                // "I requested changes and it disappeared from the table".
                await fetchData();
            } else {
                const ct = CONTENT_TYPES.find(c => c.key === contentKey);
                const path = ct ? ct.reviewPath(item.id) : `/hod/assessments/${item.id}/review`;
                await apiFetch(path, { method: "PATCH", body: JSON.stringify({ status: statusOrAction, feedback: feedbackOrReason }) });
                notify("success", `${statusOrAction === "approved" ? "Approved" : "Rejected"} successfully`);
                setReviewModal(null);

                const affectedKey = ct?.key || "assessments";
                const affectedCt = CONTENT_TYPES.find(c => c.key === affectedKey);
                if (affectedCt) {
                    const fresh = await apiFetch(affectedCt.apiPending);
                    setPendingContent(prev => ({ ...prev, [affectedKey]: Array.isArray(fresh) ? fresh : [] }));
                }
            }

            const ov = await apiFetch("/hod/teachers/overview");
            setOverview(ov);

            if (drawer) {
                const drawerPath = drawer.contentType === "lessonPlans"
                    ? `/hod/teachers/${drawer.teacher.id}/lesson-plans`
                    : `/hod/teachers/${drawer.teacher.id}/assessments`;
                const drawerData = await apiFetch(drawerPath);
                setDrawer(prev => prev ? { ...prev, items: Array.isArray(drawerData) ? drawerData : [] } : null);
            }
        } catch (e) {
            notify("error", e.message);
        } finally {
            setReviewSaving(false);
        }
    };

    // Bulk approve — approve-only (see backend), used for both the HOD and
    // the admin stage queues.
    const handleBulkApprove = async (ids, stage) => {
        if (!ids.length) return;
        if (!window.confirm(`Approve ${ids.length} lesson plan${ids.length > 1 ? "s" : ""}? This cannot be bulk-undone.`)) return;
        setBulkSaving(true);
        try {
            const path = stage === "admin" ? "/hod/admin/lesson-plans/bulk-review" : "/hod/lesson-plans/bulk-review";
            const result = await apiFetch(path, { method: "POST", body: JSON.stringify({ ids, expected_revisions: {} }) });
            const failed = (result.results || []).filter(r => !r.ok);
            if (failed.length) {
                notify("error", `${result.succeeded} approved, ${failed.length} failed (e.g. "${failed[0].error}")`);
            } else {
                notify("success", `${result.succeeded} lesson plan${result.succeeded > 1 ? "s" : ""} approved`);
            }
            await fetchData();
        } catch (e) {
            notify("error", e.message);
        } finally {
            setBulkSaving(false);
        }
    };

    // Derived
    const allGrades = [...new Set(teacherList.flatMap(t => t.grade_levels || []))].sort((a, b) => parseInt(a) - parseInt(b));
    const filteredTeachers = teacherList.filter(t => {
        if (gradeFilter && !t.grade_levels?.includes(gradeFilter)) return false;
        if (searchFilter && !t.name.toLowerCase().includes(searchFilter.toLowerCase()) && !t.email.toLowerCase().includes(searchFilter.toLowerCase())) return false;
        if (teacherStatusFilter && (teacherStatusFilter === "active") !== Boolean(t.is_active)) return false;
        return true;
    });
    const totalPending = Object.values(pendingContent).reduce((s, arr) => s + arr.length, 0);

    // ── Loading ───────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <>
                <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins] flex items-center justify-center">
                    <div className="text-center">
                        <RefreshCw className="w-12 h-12 text-[#D67C2C] animate-spin mx-auto mb-4" />
                        <p className="text-gray-600">Loading department data...</p>
                    </div>
                </div>
            </>
        );
    }

    // ── Main Render ───────────────────────────────────────────────────────────
    return (
        <>
            <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

                    {/* Toasts */}
                    {error   && <Toast msg={error}   type="error"   onClose={() => setError(null)}   />}
                    {success && <Toast msg={success} type="success" onClose={() => setSuccess(null)} />}

                    {/* ── Hero Header ──────────────────────────────────────────────── */}
                    <div className="mb-8">
                        <div className="bg-gradient-to-r from-orange-50 to-blue-50 rounded-2xl p-8 border border-orange-100">
                            <div className="flex items-start gap-6">
                                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#D67C2C] to-orange-600 flex items-center justify-center shadow-lg flex-shrink-0">
                                    <GraduationCap className="w-10 h-10 text-white" />
                                </div>
                                <div className="flex-1">
                                    <h1 className="text-3xl font-bold text-gray-900 mb-1">
                                        {myDepartment?.name || "Department Overview"}
                                    </h1>
                                    <p className="text-gray-600 text-base mb-4">
                                        {myDepartment
                                            ? `Supervise and manage all teachers and content in your department`
                                            : "No department assigned — contact your administrator to get set up"}
                                    </p>
                                    <div className="flex flex-wrap gap-4 text-sm">
                                        {myDepartment?.subjects?.length > 0 && (
                                            <div className="flex items-center gap-2 text-gray-700"><div className="w-2 h-2 rounded-full bg-[#D67C2C]"></div><span>{myDepartment.subjects.join(", ")}</span></div>
                                        )}
                                        {myDepartment?.grade_levels?.length > 0 && (
                                            <div className="flex items-center gap-2 text-gray-700"><div className="w-2 h-2 rounded-full bg-blue-500"></div><span>Grades: {myDepartment.grade_levels.join(", ")}</span></div>
                                        )}
                                        {totalPending > 0 && (
                                            <div className="flex items-center gap-2 text-amber-700"><div className="w-2 h-2 rounded-full bg-amber-500"></div><span>{totalPending} items pending your review</span></div>
                                        )}
                                    </div>
                                </div>
                                <button onClick={fetchData}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-600 rounded-xl font-medium hover:shadow-md hover:border-[#D67C2C] hover:text-[#D67C2C] transition-all duration-200 flex-shrink-0">
                                    <RefreshCw className="w-4 h-4" /> Refresh
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ── Stats ────────────────────────────────────────────────────── */}
                    {overview && (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                            {[
                                { label: "Teachers",     value: overview.total_teachers,     sub: `${overview.active_teachers} active`,   icon: <Users className="w-6 h-6 text-blue-600" />,   bg: "bg-blue-100" },
                                { label: "Lesson Plans", value: overview.total_lesson_plans,  sub: `${pendingContent["lesson-plans"]?.length || 0} pending review`, icon: <FileText className="w-6 h-6 text-[#D67C2C]" />, bg: "bg-orange-100" },
                                { label: "Assessments",  value: overview.total_assessments,   sub: `${pendingContent["assessments"]?.length || 0} pending review`, icon: <Target className="w-6 h-6 text-purple-600" />, bg: "bg-purple-100" },
                                { label: "Classes",      value: overview.total_classes,       sub: "active classes",                       icon: <GraduationCap className="w-6 h-6 text-green-600" />, bg: "bg-green-100" },
                            ].map(({ label, value, sub, icon, bg }) => (
                                <div key={label} className="bg-white rounded-xl shadow-md border border-gray-200 p-5">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">{label}</p>
                                            <p className="text-3xl font-bold text-gray-900 mt-1">{value ?? 0}</p>
                                            <p className="text-xs text-gray-400 mt-1">{sub}</p>
                                        </div>
                                        <div className={`p-3 rounded-full ${bg}`}>{icon}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ── Supervision Summary ───────────────────────────────────────── */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
                        <div className="flex items-center gap-2 mb-4">
                            <ShieldCheck className="w-5 h-5 text-[#D67C2C]" />
                            <h3 className="font-semibold text-gray-800">Supervision Queue</h3>
                            {totalPending > 0 && (
                                <span className="ml-auto bg-amber-100 text-amber-700 text-xs font-bold px-2.5 py-0.5 rounded-full">{totalPending} pending</span>
                            )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                            {CONTENT_TYPES.map(ct => {
                                const count = pendingContent[ct.key]?.length || 0;
                                const Icon = ct.icon;
                                return (
                                    <button key={ct.key} onClick={() => setActiveTab(ct.key)}
                                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border text-center transition-all ${activeTab === ct.key ? "bg-orange-50 border-[#D67C2C] shadow-sm" : "border-gray-200 hover:border-orange-200 hover:bg-orange-50/40"}`}>
                                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${count > 0 ? "bg-amber-100" : "bg-gray-100"}`}>
                                            <Icon size={17} className={count > 0 ? "text-amber-600" : "text-gray-400"} />
                                        </div>
                                        <div>
                                            <p className="text-xs font-semibold text-gray-700">{ct.label}</p>
                                            <p className={`text-xs mt-0.5 font-medium ${count > 0 ? "text-amber-600" : "text-gray-400"}`}>
                                                {count > 0 ? `${count} pending` : "All clear"}
                                            </p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* ── Tab Bar ───────────────────────────────────────────────────── */}
                    <div className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto">
                        {[
                            { key: "teachers", label: "Teachers", icon: Users, badge: 0 },
                            ...CONTENT_TYPES.map(ct => ({ key: ct.key, label: ct.label, icon: ct.icon, badge: pendingContent[ct.key]?.length || 0 })),
                            ...(canReviewAdminStage ? [{ key: "lesson-plans-admin", label: "Admin Approval", icon: ShieldCheck, badge: pendingAdminLessonPlans.length }] : []),
                        ].map(({ key, label, icon: Icon, badge }) => (
                            <button key={key} onClick={() => setActiveTab(key)}
                                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap -mb-px
                                    ${activeTab === key ? "border-[#D67C2C] text-[#D67C2C]" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}>
                                <Icon size={15} />
                                {label}
                                {badge > 0 && <span className={`ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${activeTab === key ? "bg-orange-100 text-[#D67C2C]" : "bg-amber-100 text-amber-700"}`}>{badge}</span>}
                            </button>
                        ))}
                    </div>

                    {/* ── Tab: Teachers ────────────────────────────────────────────── */}
                    {activeTab === "teachers" && (
                        <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
                            {/* Filters */}
                            <div className="p-5 border-b border-gray-100 bg-gray-50">
                                <div className="flex flex-wrap items-center gap-3">
                                    <div className="flex items-center gap-2">
                                        <Filter className="w-4 h-4 text-[#D67C2C]" />
                                        <span className="text-sm font-medium text-gray-700">Filters</span>
                                    </div>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input type="text" placeholder="Search teacher..."
                                            value={searchFilter}
                                            onChange={e => setSearchFilter(e.target.value)}
                                            className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent w-48" />
                                    </div>
                                    <select value={gradeFilter} onChange={e => setGradeFilter(e.target.value)}
                                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent">
                                        <option value="">All Grades</option>
                                        {allGrades.map(g => <option key={g} value={g}>Grade {g}</option>)}
                                    </select>
                                    <select value={teacherStatusFilter} onChange={e => setTeacherStatusFilter(e.target.value)}
                                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent">
                                        <option value="">All Status</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                    {(gradeFilter || searchFilter || teacherStatusFilter) && (
                                        <button onClick={() => { setGradeFilter(""); setSearchFilter(""); setTeacherStatusFilter(""); }}
                                            className="text-sm text-[#D67C2C] hover:underline font-medium">
                                            Clear filters
                                        </button>
                                    )}
                                    <div className="ml-auto flex items-center gap-2">
                                        <p className="text-sm text-gray-500">{filteredTeachers.length} teacher{filteredTeachers.length !== 1 ? "s" : ""}</p>
                                        <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm">
                                            <RefreshCw className="w-4 h-4" /> Refresh
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {filteredTeachers.length === 0 ? (
                                <div className="text-center py-16">
                                    <Users className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                                    <p className="text-gray-500 text-lg font-medium">
                                        {teacherList.length === 0 ? "No teachers assigned" : "No teachers match your filters"}
                                    </p>
                                    <p className="text-gray-400 text-sm mt-1">
                                        {teacherList.length === 0 ? "Ask your administrator to assign teachers to your department" : "Try adjusting your filters"}
                                    </p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50 border-b border-gray-200">
                                            <tr>
                                                {["Teacher", "Grades", "Classes", "Lesson Plans", "Assessments", "Status", ""].map(h => (
                                                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-100">
                                            {filteredTeachers.map(teacher => (
                                                <TeacherRow key={teacher.id} teacher={teacher} onViewContent={handleViewTeacherContent} />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Tabs: Content Review ─────────────────────────────────────── */}
                    {CONTENT_TYPES.map(ct => activeTab === ct.key && (
                        <div key={ct.key} className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
                            {GATED_TABS[ct.key] ? (
                                <ApprovalsDashboard embedded config={GATED_TABS[ct.key]} />
                            ) : ct.key === "lesson-plans" ? (
                                <>
                                    <div className="p-5 border-b border-gray-100 bg-gray-50">
                                        <div className="flex items-center justify-between gap-3 mb-3">
                                            <div>
                                                <h2 className="text-lg font-semibold text-gray-900">Lesson Plans</h2>
                                                <p className="text-sm text-gray-500 mt-0.5">
                                                    {pendingContent["lesson-plans"]?.length || 0} awaiting your review · {allLessonPlans.length} total in your department
                                                </p>
                                            </div>
                                            <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm">
                                                <RefreshCw className="w-4 h-4" /> Refresh
                                            </button>
                                        </div>
                                        {/* Clickable status filter pills — every status a lesson plan can
                                            be in, not just the ones currently non-zero, so it's obvious
                                            this table can show all of them. Click a pill to filter the
                                            table to it; click again (or "All") to clear. */}
                                        <div className="flex flex-wrap gap-2">
                                            <button onClick={() => setLpStatusFilter("")}
                                                className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${!lpStatusFilter ? "bg-gray-800 text-white border-gray-800" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
                                                All ({allLessonPlans.length})
                                            </button>
                                            {STATUS_FILTER_ORDER.map(s => {
                                                const count = allLessonPlans.filter(i => i.status === s).length;
                                                if (count === 0) return null;
                                                const active = lpStatusFilter === s;
                                                return (
                                                    <button key={s} onClick={() => setLpStatusFilter(active ? "" : s)}
                                                        className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-[#D67C2C] text-white border-[#D67C2C]" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
                                                        <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-white" : STATUS_DOT[s] || "bg-gray-400"}`} />
                                                        {STATUS_LABELS[s] || s} ({count})
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <ContentTable
                                        items={allLessonPlans}
                                        contentKey="lesson-plans"
                                        onReview={item => openReview(item, "lesson-plans")}
                                        onBulkApprove={ids => handleBulkApprove(ids, "hod")}
                                        bulkSaving={bulkSaving}
                                        actionableStatuses={["pending_approval"]}
                                        onViewDiary={setDiaryItem}
                                        statusFilter={lpStatusFilter}
                                        onStatusFilterChange={setLpStatusFilter}
                                        emptyTitle="No lesson plans yet"
                                        emptyText="Nothing has been created in your department yet."
                                        jumpToTabForStatus={canReviewAdminStage ? { pending_admin: () => setActiveTab("lesson-plans-admin") } : {}}
                                    />
                                </>
                            ) : (
                                <>
                                    <div className="p-5 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                                        <div>
                                            <h2 className="text-lg font-semibold text-gray-900">{ct.label} — Pending Review</h2>
                                            <p className="text-sm text-gray-500 mt-0.5">
                                                {pendingContent[ct.key]?.length || 0} item{pendingContent[ct.key]?.length !== 1 ? "s" : ""} awaiting your decision
                                            </p>
                                        </div>
                                        <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm">
                                            <RefreshCw className="w-4 h-4" /> Refresh
                                        </button>
                                    </div>
                                    <ContentTable
                                        items={pendingContent[ct.key] || []}
                                        contentKey={ct.key}
                                        onReview={item => openReview(item, ct.key)}
                                    />
                                </>
                            )}
                        </div>
                    ))}

                    {/* ── Tab: Admin Approval (2nd, independent stage) ─────────────── */}
                    {canReviewAdminStage && activeTab === "lesson-plans-admin" && (
                        <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
                            <div className="p-5 border-b border-gray-100 bg-gray-50">
                                <div className="flex items-center justify-between gap-3 mb-3">
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900">Admin Approval — Second Stage</h2>
                                        <p className="text-sm text-gray-500 mt-0.5">
                                            {pendingAdminLessonPlans.length} awaiting the final admin sign-off · {allAdminLessonPlans.length} total.
                                            A different person than the HOD reviewer must complete this stage.
                                        </p>
                                    </div>
                                    <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm">
                                        <RefreshCw className="w-4 h-4" /> Refresh
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button onClick={() => setLpAdminStatusFilter("")}
                                        className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${!lpAdminStatusFilter ? "bg-gray-800 text-white border-gray-800" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
                                        All ({allAdminLessonPlans.length})
                                    </button>
                                    {STATUS_FILTER_ORDER.map(s => {
                                        const count = allAdminLessonPlans.filter(i => i.status === s).length;
                                        if (count === 0) return null;
                                        const active = lpAdminStatusFilter === s;
                                        return (
                                            <button key={s} onClick={() => setLpAdminStatusFilter(active ? "" : s)}
                                                className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-[#D67C2C] text-white border-[#D67C2C]" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-white" : STATUS_DOT[s] || "bg-gray-400"}`} />
                                                {STATUS_LABELS[s] || s} ({count})
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <ContentTable
                                items={allAdminLessonPlans}
                                contentKey="lesson-plans"
                                icon={ShieldCheck}
                                onReview={item => openReview(item, "lesson-plans-admin")}
                                onBulkApprove={ids => handleBulkApprove(ids, "admin")}
                                bulkSaving={bulkSaving}
                                actionableStatuses={["pending_admin"]}
                                onViewDiary={setDiaryItem}
                                statusFilter={lpAdminStatusFilter}
                                onStatusFilterChange={setLpAdminStatusFilter}
                                emptyTitle="No lesson plans yet"
                                emptyText="Nothing has reached the admin stage yet."
                                jumpToTabForStatus={{ pending_approval: () => setActiveTab("lesson-plans") }}
                            />
                        </div>
                    )}

                    {/* ── Quick Management Links ───────────────────────────────────── */}
                    <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <BarChart3 className="w-5 h-5 text-[#D67C2C]" />
                            <h3 className="font-semibold text-gray-800">Quick Access</h3>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {[
                                { label: "Lesson Plans",  path: "/LessonPlanManagement",  icon: FileText },
                                { label: "Assessments",   path: "/AssessmentManagement",   icon: Target },
                                { label: "Competencies",  path: "/CompetencyManagement",   icon: Award },
                                { label: "Classes",       path: "/ClassManagement",         icon: GraduationCap },
                            ].map(({ label, path, icon: Icon }) => (
                                <button key={label} onClick={() => navigate(path)}
                                    className="flex items-center gap-3 p-4 border border-gray-200 rounded-xl hover:border-[#D67C2C] hover:bg-orange-50 transition-all text-left group">
                                    <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D67C2C] transition-colors">
                                        <Icon size={17} className="text-[#D67C2C] group-hover:text-white transition-colors" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-900 text-sm">{label}</p>
                                        <p className="text-xs text-gray-400 flex items-center gap-0.5">Open <ChevronRight size={10} /></p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                </div>
            </div>

            {/* Teacher Content Drawer */}
            {drawer && (
                <TeacherContentDrawer
                    teacher={drawer.teacher}
                    contentType={drawer.contentType}
                    items={drawer.items}
                    loading={drawer.loading}
                    onClose={() => setDrawer(null)}
                    onReview={item => openReview(item, drawer.contentType === "lessonPlans" ? "lesson-plans" : "assessments")}
                    onViewDiary={setDiaryItem}
                />
            )}

            {diaryItem && <DiaryModal item={diaryItem} onClose={() => setDiaryItem(null)} />}

            {/* Review Modal */}
            {reviewModal && (
                <ReviewModal
                    item={reviewModal.item}
                    contentLabel={reviewModal.contentKey.startsWith("lesson-plans") ? "Lesson Plan" : (CONTENT_TYPES.find(c => c.key === reviewModal.contentKey)?.label || "Content")}
                    isLessonPlanGate={reviewModal.contentKey === "lesson-plans" || reviewModal.contentKey === "lesson-plans-admin"}
                    stageLabel={reviewModal.contentKey === "lesson-plans-admin" ? "Admin Stage" : reviewModal.contentKey === "lesson-plans" ? "HOD Stage" : null}
                    onClose={() => setReviewModal(null)}
                    onConfirm={handleConfirmReview}
                    saving={reviewSaving}
                />
            )}
        </>
    );
};

export default HODDashboard;
